import axios from 'axios';
import { find, some, startsWith } from 'lodash';

import configService from '@lib/services/configService';
import storage from '@lib/services/storageService';

interface RecentlyUsedObject {
  cardName: string;
  title: string;
  subtitle: string;
  routes: Array<string>;
  security: string;
}

const recentlyUsedObject: Array<RecentlyUsedObject> = [
  {
    cardName: 'providers',
    title: 'Providers',
    subtitle: 'Manage provider preferences',
    routes: ['/v2/settings/practice-resources/providers'],
    security: 'practiceResources.providers',
  },
  {
    cardName: 'users',
    title: 'Users',
    subtitle: 'Add or manage users',
    routes: ['/v2/settings/security-center/users'],
    security: 'securityCenter.users',
  },
  {
    cardName: 'groups',
    title: 'Groups',
    subtitle: 'Add or manage groups',
    routes: ['/v2/settings/security-center/groups'],
    security: 'securityCenter.groups',
  },
  {
    cardName: 'tokens',
    title: 'API Tokens',
    subtitle: 'Generate and rotate API tokens',
    routes: ['/v2/settings/security-center/tokens'],
    security: 'securityCenter.tokens',
  },
  {
    cardName: 'facilities',
    title: 'Facilities',
    subtitle: 'Manage facilities preferences',
    routes: ['/v2/settings/practice-resources/facilities'],
    security: 'practiceResources.facilities',
  },
  {
    cardName: 'specialties',
    title: 'Specialties',
    subtitle: 'Manage specialties preferences',
    routes: ['/v2/settings/practice-resources/specialties'],
    security: 'practiceResources.specialties',
  },
  {
    cardName: 'apptTypes',
    title: 'Appt Types',
    subtitle: 'Manage appt types',
    routes: ['/v2/settings/practice-resources/appointment-types'],
    security: 'practiceResources.apptTypes',
  },
  {
    cardName: 'general',
    title: 'General',
    subtitle: 'Manage general settings',
    routes: ['/v2/settings/features/general'],
    security: 'featureSettings.general',
  },
  {
    cardName: 'communications',
    title: 'Communication',
    subtitle: 'Manage communication settings',
    routes: ['/v2/settings/features/communication'],
    security: 'featureSettings.communication',
  },
  {
    cardName: 'reminders',
    title: 'Reminders',
    subtitle: 'Manage reminder settings',
    routes: ['/v2/settings/features/reminder'],
    security: 'featureSettings.reminders',
  },
  {
    cardName: 'inboundReferral',
    title: 'Inbound Referral',
    subtitle: 'Manage inbound referral settings',
    routes: ['/v2/settings/features/inbound-referral'],
    security: 'featureSettings.inboundReferral',
  },
  {
    cardName: 'outboundReferral',
    title: 'Outbound Referral',
    subtitle: 'Manage outbound referral settings',
    routes: ['/v2/settings/features/outbound-referral'],
    security: 'featureSettings.outboundReferral',
  },
  {
    cardName: 'schedulerAssist',
    title: 'Scheduler Assist',
    subtitle: 'Manage scheduler assist settings',
    routes: ['/v2/settings/features/scheduler-assist'],
    security: 'featureSettings.schedulerAssist',
  },
  {
    cardName: 'scheduler',
    title: 'Scheduler',
    subtitle: 'Manage scheduler settings',
    routes: ['/v2/settings/features/scheduler'],
    security: 'featureSettings.scheduler',
  },
  {
    cardName: 'waitlist',
    title: 'Waitlist',
    subtitle: 'Manage waitlist settings',
    routes: ['/v2/settings/features/waitlist'],
    security: 'featureSettings.waitlist',
  },
  {
    cardName: 'waitingRoom',
    title: 'Waiting Room',
    subtitle: 'Manage waiting room settings',
    routes: ['/v2/settings/features/waiting-room'],
    security: 'featureSettings.waitingRoom',
  },
  {
    cardName: 'telehealth',
    title: 'Telehealth',
    subtitle: 'Manage telehealth settings',
    routes: ['/v2/settings/features/telehealth'],
    security: 'featureSettings.telehealth',
  },
  {
    cardName: 'feedback',
    title: 'Feedback',
    subtitle: 'Manage feedback settings',
    routes: ['/v2/settings/features/feedback'],
    security: 'featureSettings.feedback',
  },
  {
    cardName: 'followup',
    title: 'Followup',
    subtitle: 'Manage followup settings',
    routes: ['/v2/settings/features/followup'],
    security: 'featureSettings.followup',
  },
  {
    cardName: 'lumabot',
    title: 'LumaBot',
    subtitle: 'Manage LumaBot settings',
    routes: ['/v2/settings/features/lumabot'],
    security: 'featureSettings.lumabot',
  },
  {
    cardName: 'formBuilder',
    title: 'Form Builder',
    subtitle: 'Build and customize forms',
    routes: ['/v2/form-builder'],
    security: 'utilities.formBuilder',
  },
  {
    cardName: 'checklistBuilder',
    title: 'Checklist Builder',
    subtitle: 'Build and customize checklists',
    routes: ['/v2/settings/utilities/checklist-builder'],
    security: 'utilities.checklistBuilder',
  },
  {
    cardName: 'broadcastLibrary',
    title: 'Broadcast Library',
    subtitle: 'Create and customize broadcast libraries',
    routes: ['/v2/settings/utilities/message-library/broadcast'],
    security: 'utilities.messageLibrary.broadcastLibrary',
  },
  {
    cardName: 'hubLibrary',
    title: 'Hub Library',
    subtitle: 'Create and customize hub libraries',
    routes: ['/v2/settings/utilities/message-library/chat'],
    security: 'utilities.messageLibrary.hubLibrary',
  },
  {
    cardName: 'appointmentTemplates',
    title: 'Appointment Templates',
    subtitle: 'Create and customize appointment templates',
    routes: ['/v2/settings/utilities/message-templates/appointments'],
    security: 'utilities.messageTemplates.appointments',
  },
  {
    cardName: 'offersTemplates',
    title: 'Offers Templates',
    subtitle: 'Create and customize offers templates',
    routes: ['/v2/settings/utilities/message-templates/offers'],
    security: 'utilities.messageTemplates.offers',
  },
  {
    cardName: 'waitlistsTemplates',
    title: 'Waitlists Templates',
    subtitle: 'Create and customize waitlists templates',
    routes: ['/v2/settings/utilities/message-templates/waitlists'],
    security: 'utilities.messageTemplates.waitlists',
  },
  {
    cardName: 'referralsTemplates',
    title: 'Referrals Templates',
    subtitle: 'Create and customize referrals templates',
    routes: ['/v2/settings/utilities/message-templates/referrals'],
    security: 'utilities.messageTemplates.referrals',
  },
  {
    cardName: 'outreachTemplates',
    title: 'Outreach Templates',
    subtitle: 'Create and customize outreach templates',
    routes: ['/v2/settings/utilities/message-templates/outreaches'],
    security: 'utilities.messageTemplates.outreach',
  },
  {
    cardName: 'feedbackTemplates',
    title: 'Feedback Templates',
    subtitle: 'Create and customize feedback templates',
    routes: ['/v2/settings/utilities/message-templates/feedback'],
    security: 'utilities.messageTemplates.feedback',
  },
  {
    cardName: 'otherTemplates',
    title: 'Other Templates',
    subtitle: 'Create and customize other templates',
    routes: ['/v2/settings/utilities/message-templates/other'],
    security: 'utilities.messageTemplates.other',
  },
  {
    cardName: 'patientForm',
    title: 'Patient Form Branding',
    subtitle: 'Customize patient form branding preferences',
    routes: ['/v2/settings/utilities/branding/patient-form'],
    security: 'utilities.branding.patientForm',
  },
  {
    cardName: 'cobranding',
    title: 'Cobranding',
    subtitle: 'Customize cobranding preferences',
    routes: ['/v2/settings/utilities/branding/cobranding'],
    security: 'utilities.branding.cobranding',
  },
  {
    cardName: 'patientBilling',
    title: 'Patient Billing',
    subtitle: 'Manage patient billing preferences',
    routes: ['/v2/settings/utilities/patient-billing/items'],
    security: 'utilities.patientBilling',
  },
  {
    cardName: 'customActions',
    title: 'Custom Actions',
    subtitle: 'Create and customize custom actions',
    routes: ['/v2/settings/utilities/custom-actions'],
    security: 'utilities.customActions',
  },
  {
    cardName: 'automations',
    title: 'Automations',
    subtitle: 'Create and customize automations',
    routes: ['/v2/settings/utilities/automations'],
    security: 'utilities.automations',
  },
  {
    cardName: 'messagePartials',
    title: 'Message Partials',
    subtitle: 'Create and customize message partials',
    routes: ['/v2/settings/utilities/message-template-partials'],
    security: 'utilities.messagePartials',
  },
  {
    cardName: 'lumabotBuilder',
    title: 'LumaBot Builder',
    subtitle: 'Build and customize chatflows',
    routes: ['/v2/settings/utilities/lumabot-builder'],
    security: 'utilities.lumaBot',
  },
  {
    cardName: 'lumabotTemplates',
    title: 'LumaBot Templates',
    subtitle: 'Create and customize Lumabot templates',
    routes: ['/v2/settings/utilities/message-templates/lumabot'],
    security: 'utilities.messageTemplates.lumabot',
  },
  {
    cardName: 'availabilityViewer',
    title: 'Availability Viewer',
    subtitle: 'Manage Availabilities',
    routes: ['/v2/settings/utilities/availabilities-viewer'],
    security: 'utilities.availabilitiesViewers',
  },
];

export const getRecentlyUsedObjectByCardName = (cardName: string): RecentlyUsedObject | undefined =>
  find(recentlyUsedObject, ['cardName', cardName]);

export const getRecentlyUsedObjectByRoute = (searchedRoute: string): RecentlyUsedObject | undefined =>
  find(recentlyUsedObject, (obj) => some(obj.routes, (route) => startsWith(searchedRoute, route)));

let isPostingRecentlyUsed = false;
let lastPath = '';

export default function recentlyUsedRequest() {
  const path = window.location.pathname;

  const isInSettings = startsWith(path, '/settings') || startsWith(path, '/v2/settings');
  const isNewFormBuilder = startsWith(path, '/v2/form');
  const shouldNotSendRequest = (!isInSettings && !isNewFormBuilder) || isPostingRecentlyUsed || lastPath === path;
  if (shouldNotSendRequest) return;

  const recentlyUsed = getRecentlyUsedObjectByRoute(path);
  if (!recentlyUsed) return;

  isPostingRecentlyUsed = true;
  lastPath = path;
  const { cardName } = recentlyUsed;
  const url = `${configService.getApiBasePath()}/settings/recently-used`;
  const options = {
    headers: {
      'x-access-token': storage.getToken(),
      'Content-Type': 'application/json',
    },
  };

  axios
    .post(url, { cardName }, options)
    .then(() => (isPostingRecentlyUsed = false)) // eslint-disable-line
    .catch(() => (isPostingRecentlyUsed = false)); //eslint-disable-line
}
